import { useEffect, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  Modal,
  PropertyList,
  PropertyListItem,
  SelectField,
  Spinner,
  Switch,
  Tag,
  TextField,
} from '@humanforce/mango-ui-react';
import type {
  EftFormat,
  EmployerBankingDetailsDto,
  SpCreditorDesignationDto,
  BankAccountOptionDto,
} from '../organisationApiTypes';
import {
  addNominatedDeduction,
  addSpCreditor,
  getEmployerBanking,
  saveEmployerBanking,
  updateNominatedDeduction,
  updateSpCreditor,
  listBankAccounts,
  saveBankAccount,
} from '../employerBankingApi';

interface EmployerBankingPanelProps {
  businessRecordId: string;
}

const EFT_FORMAT_OPTIONS: { label: string; value: EftFormat }[] = [
  { label: 'Self-balancing ABA', value: 'self_balancing_aba' },
  { label: 'CBA Direct Entry', value: 'cba_direct_entry' },
  { label: 'ANZ Direct Credit', value: 'anz_direct_credit' },
  { label: 'NAB Direct Entry', value: 'nab_direct_entry' },
  { label: 'Westpac Direct Credit', value: 'westpac_direct_credit' },
];

const formatLabel = (value?: EftFormat | null) =>
  EFT_FORMAT_OPTIONS.find((option) => option.value === value)?.label ?? '—';

// Advisory tone mapping. CatalogueUnverified means a rule could not be fully
// evaluated (no Banking Master catalogue exists yet) so it reads as caution;
// DesignatedCreditorWithoutNominations is a benign heads-up so it reads as info.
const advisoryTone = (code: string): 'caution' | 'info' =>
  code === 'DesignatedCreditorWithoutNominations' ? 'info' : 'caution';

interface ConfigDraft {
  eftEnabled: boolean;
  runsOwnEft: boolean;
  salaryPackagingEftEnabled: boolean;
  specialCalculationEftEnabled: boolean;
  eftFormat: EftFormat | '';
  selfBalancingReference: string;
  salaryPackagingEftReference: string;
  employeePayDebitAccountId: string;
  financialInstitutionAbbreviation: string;
  directEntryUserId: string;
  abaUserName: string;
  abaFileDescription: string;
  standardWageNarrativeTemplate: string;
  paygRemittanceCreditorId: string;
  superRemittanceCreditorId: string;
}

const draftFromDetails = (details: EmployerBankingDetailsDto | null): ConfigDraft => ({
  eftEnabled: details?.eftEnabled ?? true,
  runsOwnEft: details?.runsOwnEft ?? true,
  salaryPackagingEftEnabled: details?.salaryPackagingEftEnabled ?? false,
  specialCalculationEftEnabled: details?.specialCalculationEftEnabled ?? true,
  eftFormat: details?.eftFormat ?? 'self_balancing_aba',
  selfBalancingReference: details?.selfBalancingReference ?? '',
  salaryPackagingEftReference: details?.salaryPackagingEftReference ?? '',
  employeePayDebitAccountId: details?.employeePayDebitAccountId ?? '',
  financialInstitutionAbbreviation: details?.financialInstitutionAbbreviation ?? '',
  directEntryUserId: details?.directEntryUserId ?? '',
  abaUserName: details?.abaUserName ?? '',
  abaFileDescription: details?.abaFileDescription ?? '',
  standardWageNarrativeTemplate: details?.standardWageNarrativeTemplate ?? '{WorkerName} {PeriodStart}-{PeriodEnd}',
  paygRemittanceCreditorId: details?.paygRemittanceCreditorId ?? '',
  superRemittanceCreditorId: details?.superRemittanceCreditorId ?? '',
});

const CONSOLIDATION_UNSUPPORTED =
  'Consolidating into a parent Business Record is not supported. This Business Record must run its own EFT.';

const configErrors = (draft: ConfigDraft): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (draft.eftEnabled && !draft.eftFormat) errors.eftFormat = 'Bank file format is required when EFT is enabled.';
  if (draft.eftFormat === 'self_balancing_aba' && !draft.selfBalancingReference.trim())
    errors.selfBalancingReference = 'Required for the self-balancing ABA format.';
  if (draft.selfBalancingReference.length > 18) errors.selfBalancingReference = 'Cannot exceed 18 characters.';
  if (draft.salaryPackagingEftEnabled && !draft.salaryPackagingEftReference.trim())
    errors.salaryPackagingEftReference = 'Required when Salary Packaging EFT is enabled.';
  if (draft.salaryPackagingEftReference.length > 18) errors.salaryPackagingEftReference = 'Cannot exceed 18 characters.';
  if (draft.eftEnabled && draft.runsOwnEft && !draft.employeePayDebitAccountId.trim())
    errors.employeePayDebitAccountId = 'Required when this Business Record runs its own EFT.';
  if (draft.eftEnabled && draft.financialInstitutionAbbreviation.trim().length !== 3) errors.financialInstitutionAbbreviation = 'Enter the 3-character institution abbreviation.';
  if (draft.eftEnabled && !draft.directEntryUserId.trim()) errors.directEntryUserId = 'Direct Entry User ID is required.';
  if (draft.eftEnabled && !draft.abaUserName.trim()) errors.abaUserName = 'ABA user name is required.';
  if (draft.eftEnabled && !draft.abaFileDescription.trim()) errors.abaFileDescription = 'ABA description is required.';
  if (draft.eftEnabled && !draft.standardWageNarrativeTemplate.trim()) errors.standardWageNarrativeTemplate = 'Standard wage narrative is required.';
  if (draft.standardWageNarrativeTemplate.length > 120) errors.standardWageNarrativeTemplate = 'Cannot exceed 120 characters.';
  // BR-EB-007. The server refuses this outright — no Business Record hierarchy
  // exists to consolidate into — so it is surfaced on the toggle itself rather
  // than as a failed save the user has to interpret. Mirrored here, not owned
  // here: the refusal is still enforced server-side.
  if (!draft.runsOwnEft) errors.runsOwnEft = CONSOLIDATION_UNSUPPORTED;
  return errors;
};

export const EmployerBankingPanel = ({ businessRecordId }: EmployerBankingPanelProps) => {
  const [details, setDetails] = useState<EmployerBankingDetailsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [bankAccounts, setBankAccounts] = useState<BankAccountOptionDto[]>([]);
  const [accountModal, setAccountModal] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountDraft, setAccountDraft] = useState({ accountCode: '', accountNameDisplay: '', bsb: '', accountNumber: '', accountNameBank: '', isActive: true });

  const [configModal, setConfigModal] = useState(false);
  const [configDraft, setConfigDraft] = useState<ConfigDraft>(draftFromDetails(null));
  const [configSubmitted, setConfigSubmitted] = useState(false);
  const [formatConfirmOpen, setFormatConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [creditorModal, setCreditorModal] = useState(false);
  const [creditorDraft, setCreditorDraft] = useState({ creditorId: '', isDefaultSpCreditor: false });
  const [creditorSubmitted, setCreditorSubmitted] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<SpCreditorDesignationDto | null>(null);

  const [nominationModal, setNominationModal] = useState(false);
  const [nominationDraft, setNominationDraft] = useState({ payTypeId: '' });
  const [nominationSubmitted, setNominationSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getEmployerBanking(businessRecordId)
      .then((result) => { if (!cancelled) setDetails(result); })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Failed to load Employer banking configuration.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessRecordId, refresh]);

  useEffect(() => { void listBankAccounts(businessRecordId).then(setBankAccounts).catch(() => setBankAccounts([])); }, [businessRecordId, refresh]);

  const canEdit = details?.meta.actions.canEdit ?? false;
  const canAddSpCreditor = details?.meta.actions.canAddSpCreditor ?? false;
  const canAddNomination = details?.meta.actions.canAddNomination ?? false;

  const openEditConfig = () => {
    setConfigDraft(draftFromDetails(details));
    setConfigSubmitted(false);
    setSaveError(null);
    setConfigModal(true);
  };

  const commitConfigSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await saveEmployerBanking(businessRecordId, {
        eftEnabled: configDraft.eftEnabled,
        runsOwnEft: configDraft.runsOwnEft,
        salaryPackagingEftEnabled: configDraft.salaryPackagingEftEnabled,
        specialCalculationEftEnabled: configDraft.specialCalculationEftEnabled,
        eftFormat: configDraft.eftFormat || undefined,
        selfBalancingReference: configDraft.selfBalancingReference.trim() || undefined,
        salaryPackagingEftReference: configDraft.salaryPackagingEftReference.trim() || undefined,
        employeePayDebitAccountId: configDraft.employeePayDebitAccountId.trim() || undefined,
        financialInstitutionAbbreviation: configDraft.financialInstitutionAbbreviation.trim() || undefined,
        directEntryUserId: configDraft.directEntryUserId.trim() || undefined,
        abaUserName: configDraft.abaUserName.trim() || undefined,
        abaFileDescription: configDraft.abaFileDescription.trim() || undefined,
        standardWageNarrativeTemplate: configDraft.standardWageNarrativeTemplate.trim() || undefined,
        paygRemittanceCreditorId: configDraft.paygRemittanceCreditorId.trim() || undefined,
        superRemittanceCreditorId: configDraft.superRemittanceCreditorId.trim() || undefined,
      });
      setConfigModal(false);
      setFormatConfirmOpen(false);
      setRefresh((value) => value + 1);
    } catch (reason) {
      // The config modal stays open on failure, so the message is rendered
      // inside it — at panel level it would sit behind the modal overlay and
      // the user would see nothing happen. The format-change prompt is
      // dismissed because it has already been answered.
      setFormatConfirmOpen(false);
      setSaveError(reason instanceof Error ? reason.message : 'Failed to save Employer banking configuration.');
    } finally {
      setSaving(false);
    }
  };

  const commitAccount = async () => {
    setSaving(true); setSaveError(null);
    try {
      const saved = await saveBankAccount(businessRecordId, { ...accountDraft, accountNumber: accountDraft.accountNumber || undefined }, editingAccountId ?? undefined);
      setConfigDraft((draft) => ({ ...draft, employeePayDebitAccountId: saved.bankAccountId }));
      setAccountModal(false); setRefresh((value) => value + 1);
    } catch (reason) { setSaveError(reason instanceof Error ? reason.message : 'Failed to save bank account.'); }
    finally { setSaving(false); }
  };

  const saveConfig = () => {
    if (saving) return;
    setConfigSubmitted(true);
    if (Object.keys(configErrors(configDraft)).length > 0) return;
    // AC-013: an EftFormat change is high-impact (Banking Master cache
    // invalidation), so it is confirmed before committing.
    const formatChanged = !!details?.employerBankAccountId && details.eftFormat !== (configDraft.eftFormat || undefined);
    if (formatChanged) { setFormatConfirmOpen(true); return; }
    void commitConfigSave();
  };

  const liveDesignations = details?.spCreditors.filter((x) => x.isActive && !x.effectiveTo) ?? [];
  const existingDefault = liveDesignations.find((x) => x.isDefaultSpCreditor);
  // AC-015: surfaced inline before submission, not only as a server refusal.
  const creditorDefaultConflict = creditorDraft.isDefaultSpCreditor && existingDefault
    ? `${existingDefault.creditorId} is already the default Salary Packaging creditor.`
    : null;

  const saveCreditor = async () => {
    if (saving) return;
    setCreditorSubmitted(true);
    if (!creditorDraft.creditorId.trim() || creditorDefaultConflict) return;
    setSaving(true);
    setSaveError(null);
    try {
      await addSpCreditor(businessRecordId, {
        creditorId: creditorDraft.creditorId.trim(),
        isDefaultSpCreditor: creditorDraft.isDefaultSpCreditor,
      });
      setCreditorModal(false);
      setRefresh((value) => value + 1);
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : 'Failed to add Salary Packaging creditor.');
    } finally {
      setSaving(false);
    }
  };

  const deactivateCreditor = async () => {
    if (saving || !deactivateTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateSpCreditor(businessRecordId, deactivateTarget.designationId, { isActive: false });
      setDeactivateTarget(null);
      setRefresh((value) => value + 1);
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : 'Failed to deactivate the designation.');
    } finally {
      setSaving(false);
    }
  };

  const saveNomination = async () => {
    if (saving) return;
    setNominationSubmitted(true);
    if (!nominationDraft.payTypeId.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await addNominatedDeduction(businessRecordId, { payTypeId: nominationDraft.payTypeId.trim() });
      setNominationModal(false);
      setRefresh((value) => value + 1);
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : 'Failed to add the nominated deduction.');
    } finally {
      setSaving(false);
    }
  };

  const endDateNomination = async (nominationId: string) => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateNominatedDeduction(businessRecordId, nominationId, { effectiveTo: new Date().toISOString() });
      setRefresh((value) => value + 1);
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : 'Failed to end-date the nomination.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mgo-cluster" role="status" aria-live="polite">
        <Spinner />
        <span>Loading Employer banking…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Callout
        tone="critical"
        title="Employer banking could not be loaded"
        action={<Button label="Retry" size="small" variant="secondary" onClick={() => setRefresh((value) => value + 1)} />}
      >
        {error}
      </Callout>
    );
  }

  const errors = configSubmitted ? configErrors(configDraft) : {};
  // BR-EB-007 is shown the moment "Runs own EFT" is switched off rather than
  // waiting for a submit, matching AC-015's inline default-conflict message:
  // the server will refuse this outright, so there is nothing to discover by
  // letting the user click Save first.
  const configErrorsMap = configDraft.runsOwnEft ? errors : { ...errors, runsOwnEft: CONSOLIDATION_UNSUPPORTED };

  return (
    <div className="mgo-stack mgo-gap-l">
      <Callout tone="info" title="EFT Defaults">
        These are the EFT Defaults for this Business Record. Payrolls inherit them and may override per field.
        Source accounts are selected from canonical Banking Master records; Payrolls persist the selected account ID.
      </Callout>

      {details?.advisories.map((advisory) => (
        <Callout key={`${advisory.code}-${advisory.targetId ?? ''}`} tone={advisoryTone(advisory.code)}>
          {advisory.message}
        </Callout>
      ))}

      <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
        <Card
          header={
            <div className="mgo-split mgo-align-center">
              <span>EFT configuration</span>
              <Button label="Edit EFT configuration" variant="secondary" size="small" icon="edit" disabled={!canEdit} onClick={openEditConfig} />
            </div>
          }
        >
          <div className="mgo-stack mgo-gap-l">
            <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
              <Switch id="eft-enabled" label="EFT enabled" checked={details?.eftEnabled ?? false} disabled />
              <Switch id="runs-own-eft" label="Runs own EFT" checked={details?.runsOwnEft ?? false} disabled />
              <Switch id="salary-packaging-eft" label="Salary packaging EFT" checked={details?.salaryPackagingEftEnabled ?? false} disabled />
              <Switch id="special-calc-eft" label="Special calc EFT" checked={details?.specialCalculationEftEnabled ?? false} disabled />
            </div>
            <PropertyList>
              <PropertyListItem label="EFT format">{formatLabel(details?.eftFormat)}</PropertyListItem>
              <PropertyListItem label="Self-balancing reference">{details?.selfBalancingReference || '—'}</PropertyListItem>
              <PropertyListItem label="Salary packaging reference">{details?.salaryPackagingEftReference || '—'}</PropertyListItem>
              <PropertyListItem label="Employee pay debit account">
                {details?.employeePayDebitAccountLabel || bankAccounts.find((x) => x.bankAccountId === details?.employeePayDebitAccountId)?.label || '—'}
              </PropertyListItem>
              <PropertyListItem label="Financial institution">{details?.financialInstitutionAbbreviation || '—'}</PropertyListItem>
              <PropertyListItem label="Direct Entry User ID">{details?.directEntryUserId || '—'}</PropertyListItem>
              <PropertyListItem label="ABA user name">{details?.abaUserName || '—'}</PropertyListItem>
              <PropertyListItem label="ABA description">{details?.abaFileDescription || '—'}</PropertyListItem>
              <PropertyListItem label="Standard wage narrative">{details?.standardWageNarrativeTemplate || '—'}</PropertyListItem>
              <PropertyListItem label="PAYG remittance creditor">
                {details?.paygRemittanceCreditorId || '—'} <Tag label="reference only" tone="grey" />
              </PropertyListItem>
              <PropertyListItem label="Super remittance creditor">
                {details?.superRemittanceCreditorId || '—'} <Tag label="reference only" tone="grey" />
              </PropertyListItem>
            </PropertyList>
          </div>
        </Card>

        <Card header={<span>Salary packaging</span>}>
          <div className="mgo-stack mgo-gap-l">
            <span className="mgo-text-s">Designated creditors + nominated deductions</span>

            <div className="mgo-stack mgo-gap-m">
              <div className="mgo-split mgo-align-center">
                <span className="mgo-text-s mgo-weight-strong">Designated creditors</span>
                <Button
                  label="Add designated creditor"
                  size="small"
                  icon="add"
                  disabled={!canAddSpCreditor}
                  onClick={() => { setCreditorDraft({ creditorId: '', isDefaultSpCreditor: false }); setCreditorSubmitted(false); setSaveError(null); setCreditorModal(true); }}
                />
              </div>
              {liveDesignations.length === 0 ? (
                <EmptyState title="No designations yet" description="Add a Salary Packaging provider to route deductions to it." />
              ) : (
                <div className="mgo-stack mgo-gap-s">
                  {liveDesignations.map((designation) => (
                    <div key={designation.designationId} className="mgo-split mgo-align-center">
                      <div className="mgo-cluster mgo-gap-s">
                        <span>{designation.creditorId}</span>
                        {designation.isDefaultSpCreditor && <Tag label="Default" tone="blue" weight="strong" />}
                      </div>
                      <Button
                        label="Deactivate"
                        variant="tertiary"
                        size="small"
                        disabled={!canEdit}
                        onClick={() => setDeactivateTarget(designation)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mgo-stack mgo-gap-m">
              <div className="mgo-split mgo-align-center">
                <span className="mgo-text-s mgo-weight-strong">Nominated deductions</span>
                <Button
                  label="Add nominated deduction"
                  size="small"
                  icon="add"
                  disabled={!canAddNomination || liveDesignations.length === 0}
                  onClick={() => { setNominationDraft({ payTypeId: '' }); setNominationSubmitted(false); setSaveError(null); setNominationModal(true); }}
                />
              </div>
              {(details?.nominatedDeductions.filter((x) => !x.effectiveTo) ?? []).length === 0 ? (
                <EmptyState title="No nominated deductions yet" description="Nominate a Pay Type once a Salary Packaging provider is designated." />
              ) : (
                <div className="mgo-stack mgo-gap-s">
                  {details!.nominatedDeductions.filter((x) => !x.effectiveTo).map((nomination) => (
                    <div key={nomination.nominationId} className="mgo-split mgo-align-center">
                      <span>{nomination.payTypeId}</span>
                      <Button label="End-date" variant="tertiary" size="small" disabled={!canEdit} onClick={() => void endDateNomination(nomination.nominationId)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Modal
        visible={configModal}
        header="Edit EFT configuration"
        size="large"
        onHide={() => setConfigModal(false)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setConfigModal(false)} />
            <Button label="Save" loading={saving} disabled={saving} onClick={saveConfig} />
          </>
        }
      >
        <div className="mgo-stack mgo-gap-l">
          {saveError && <Callout tone="critical" title="Save failed">{saveError}</Callout>}
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
            <Switch id="draft-eft-enabled" label="EFT enabled" checked={configDraft.eftEnabled} onChange={(checked) => setConfigDraft((draft) => ({ ...draft, eftEnabled: checked }))} />
            <Switch
              id="draft-runs-own-eft"
              label="Runs own EFT"
              checked={configDraft.runsOwnEft}
              status={configErrorsMap.runsOwnEft ? 'critical' : undefined}
              statusText={configErrorsMap.runsOwnEft}
              onChange={(checked) => setConfigDraft((draft) => ({ ...draft, runsOwnEft: checked }))}
            />
            <Switch id="draft-salary-packaging-eft" label="Salary packaging EFT" checked={configDraft.salaryPackagingEftEnabled} onChange={(checked) => setConfigDraft((draft) => ({ ...draft, salaryPackagingEftEnabled: checked }))} />
            <Switch id="draft-special-calc-eft" label="Special calc EFT" checked={configDraft.specialCalculationEftEnabled} onChange={(checked) => setConfigDraft((draft) => ({ ...draft, specialCalculationEftEnabled: checked }))} />
          </div>
          <SelectField
            label="EFT format"
            value={configDraft.eftFormat || null}
            options={EFT_FORMAT_OPTIONS}
            searchable={false}
            status={configErrorsMap.eftFormat ? 'critical' : undefined}
            statusText={configErrorsMap.eftFormat}
            onChange={(value) => setConfigDraft((draft) => ({ ...draft, eftFormat: value as EftFormat }))}
          />
          <TextField
            label="Self-balancing reference"
            helpText="Max 18 characters, per the AU ABA spec."
            value={configDraft.selfBalancingReference}
            status={configErrorsMap.selfBalancingReference ? 'critical' : undefined}
            statusText={configErrorsMap.selfBalancingReference}
            onChange={(event) => setConfigDraft((draft) => ({ ...draft, selfBalancingReference: event.target.value }))}
          />
          <TextField
            label="Salary packaging reference"
            helpText="Max 18 characters."
            value={configDraft.salaryPackagingEftReference}
            status={configErrorsMap.salaryPackagingEftReference ? 'critical' : undefined}
            statusText={configErrorsMap.salaryPackagingEftReference}
            onChange={(event) => setConfigDraft((draft) => ({ ...draft, salaryPackagingEftReference: event.target.value }))}
          />
          <SelectField
            label="Employee pay debit account"
            helpText="Select an active Banking Master account. Account numbers are masked."
            value={configDraft.employeePayDebitAccountId || null}
            options={bankAccounts.filter((x) => x.isActive).map((x) => ({ value: x.bankAccountId, label: x.label }))}
            searchable
            status={configErrorsMap.employeePayDebitAccountId ? 'critical' : undefined}
            statusText={configErrorsMap.employeePayDebitAccountId}
            onChange={(value) => setConfigDraft((draft) => ({ ...draft, employeePayDebitAccountId: String(value ?? '') }))}
          />
          <div className="mgo-cluster mgo-justify-end">
            {bankAccounts.find((x) => x.bankAccountId === configDraft.employeePayDebitAccountId) && <Button label="Edit selected bank account" variant="secondary" onClick={() => { const selected = bankAccounts.find((x) => x.bankAccountId === configDraft.employeePayDebitAccountId)!; setEditingAccountId(selected.bankAccountId); setAccountDraft({ accountCode: selected.accountCode, accountNameDisplay: selected.accountNameDisplay, bsb: selected.bsb, accountNumber: '', accountNameBank: selected.accountNameBank, isActive: selected.isActive }); setAccountModal(true); }} />}
            <Button label="Add bank account" variant="secondary" onClick={() => { setEditingAccountId(null); setAccountDraft({ accountCode: '', accountNameDisplay: '', bsb: '', accountNumber: '', accountNameBank: '', isActive: true }); setAccountModal(true); }} />
          </div>
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
            <TextField label="Financial institution abbreviation" value={configDraft.financialInstitutionAbbreviation} status={configErrorsMap.financialInstitutionAbbreviation ? 'critical' : undefined} statusText={configErrorsMap.financialInstitutionAbbreviation} onChange={(event) => setConfigDraft((draft) => ({ ...draft, financialInstitutionAbbreviation: event.target.value }))} />
            <TextField label="Direct Entry User ID" value={configDraft.directEntryUserId} status={configErrorsMap.directEntryUserId ? 'critical' : undefined} statusText={configErrorsMap.directEntryUserId} onChange={(event) => setConfigDraft((draft) => ({ ...draft, directEntryUserId: event.target.value }))} />
            <TextField label="ABA user name" value={configDraft.abaUserName} status={configErrorsMap.abaUserName ? 'critical' : undefined} statusText={configErrorsMap.abaUserName} onChange={(event) => setConfigDraft((draft) => ({ ...draft, abaUserName: event.target.value }))} />
            <TextField label="ABA description" value={configDraft.abaFileDescription} status={configErrorsMap.abaFileDescription ? 'critical' : undefined} statusText={configErrorsMap.abaFileDescription} onChange={(event) => setConfigDraft((draft) => ({ ...draft, abaFileDescription: event.target.value }))} />
          </div>
          <TextField
            label="Standard wage narrative"
            helpText="Supported tokens: {WorkerName}, {PeriodStart}, {PeriodEnd}. Dates use YYYYMMDD."
            value={configDraft.standardWageNarrativeTemplate}
            status={configErrorsMap.standardWageNarrativeTemplate ? 'critical' : undefined}
            statusText={configErrorsMap.standardWageNarrativeTemplate}
            onChange={(event) => setConfigDraft((draft) => ({ ...draft, standardWageNarrativeTemplate: event.target.value }))}
          />
          <TextField
            label="PAYG remittance creditor"
            helpText="Reference only — typically the system ATO creditor."
            value={configDraft.paygRemittanceCreditorId}
            onChange={(event) => setConfigDraft((draft) => ({ ...draft, paygRemittanceCreditorId: event.target.value }))}
          />
          <TextField
            label="Super remittance creditor"
            helpText="Reference only — typically the system EXPSUPER creditor."
            value={configDraft.superRemittanceCreditorId}
            onChange={(event) => setConfigDraft((draft) => ({ ...draft, superRemittanceCreditorId: event.target.value }))}
          />
        </div>
      </Modal>

      <Modal visible={accountModal} header={editingAccountId ? 'Edit bank account' : 'Add bank account'} onHide={() => setAccountModal(false)} footer={<><Button label="Cancel" variant="secondary" onClick={() => setAccountModal(false)} /><Button label={editingAccountId ? 'Save account' : 'Add account'} loading={saving} disabled={saving} onClick={() => void commitAccount()} /></>}>
        <div className="mgo-stack mgo-gap-l">
          {saveError && <Callout tone="critical" title="Account could not be saved">{saveError}</Callout>}
          <TextField label="Account code" value={accountDraft.accountCode} onChange={(event) => setAccountDraft((draft) => ({ ...draft, accountCode: event.target.value }))} />
          <TextField label="Display name" value={accountDraft.accountNameDisplay} onChange={(event) => setAccountDraft((draft) => ({ ...draft, accountNameDisplay: event.target.value }))} />
          <TextField label="BSB" helpText="Six digits, with or without a hyphen." value={accountDraft.bsb} onChange={(event) => setAccountDraft((draft) => ({ ...draft, bsb: event.target.value }))} />
          <TextField label="Account number" type="password" helpText={editingAccountId ? 'Leave blank to retain the existing account number.' : undefined} value={accountDraft.accountNumber} onChange={(event) => setAccountDraft((draft) => ({ ...draft, accountNumber: event.target.value }))} />
          <TextField label="Bank account name" value={accountDraft.accountNameBank} onChange={(event) => setAccountDraft((draft) => ({ ...draft, accountNameBank: event.target.value }))} />
        </div>
      </Modal>

      <Modal
        visible={formatConfirmOpen}
        header="Confirm bank file format change"
        onHide={() => setFormatConfirmOpen(false)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setFormatConfirmOpen(false)} />
            <Button label="Confirm change" loading={saving} disabled={saving} onClick={() => void commitConfigSave()} />
          </>
        }
      >
        <p>
          Changing the bank file format invalidates Banking Master&apos;s format cache for this Business Record
          and is refused while a pay run is in progress. Continue?
        </p>
      </Modal>

      <Modal
        visible={creditorModal}
        header="Add Salary Packaging creditor"
        onHide={() => setCreditorModal(false)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setCreditorModal(false)} />
            <Button label="Add" loading={saving} disabled={saving} onClick={() => void saveCreditor()} />
          </>
        }
      >
        <div className="mgo-stack mgo-gap-l">
          <TextField
            label="Creditor reference id"
            helpText="Banking Master creditor reference id. Must be a Salary Packaging creditor."
            value={creditorDraft.creditorId}
            status={creditorSubmitted && !creditorDraft.creditorId.trim() ? 'critical' : undefined}
            statusText={creditorSubmitted && !creditorDraft.creditorId.trim() ? 'Creditor reference id is required.' : undefined}
            onChange={(event) => setCreditorDraft((draft) => ({ ...draft, creditorId: event.target.value }))}
          />
          <Switch
            id="creditor-is-default"
            label="Set as default"
            checked={creditorDraft.isDefaultSpCreditor}
            status={creditorDefaultConflict ? 'critical' : undefined}
            statusText={creditorDefaultConflict ?? undefined}
            onChange={(checked) => setCreditorDraft((draft) => ({ ...draft, isDefaultSpCreditor: checked }))}
          />
        </div>
      </Modal>

      <Modal
        visible={deactivateTarget !== null}
        header="Deactivate designation"
        onHide={() => setDeactivateTarget(null)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setDeactivateTarget(null)} />
            <Button label="Deactivate" loading={saving} disabled={saving} onClick={() => void deactivateCreditor()} />
          </>
        }
      >
        <p>
          Deactivate <strong>{deactivateTarget?.creditorId}</strong>? Nominated deductions routed to it will show a
          drift warning until reassigned or ended.
        </p>
      </Modal>

      <Modal
        visible={nominationModal}
        header="Add nominated deduction"
        onHide={() => setNominationModal(false)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setNominationModal(false)} />
            <Button label="Add" loading={saving} disabled={saving} onClick={() => void saveNomination()} />
          </>
        }
      >
        <TextField
          label="Pay Type reference id"
          helpText="Must resolve to a creditor designated for this Business Record."
          value={nominationDraft.payTypeId}
          status={nominationSubmitted && !nominationDraft.payTypeId.trim() ? 'critical' : undefined}
          statusText={nominationSubmitted && !nominationDraft.payTypeId.trim() ? 'Pay Type reference id is required.' : undefined}
          onChange={(event) => setNominationDraft((draft) => ({ ...draft, payTypeId: event.target.value }))}
        />
      </Modal>
    </div>
  );
};
